import { useRef, useEffect } from 'react';

interface Particle {
  x: number; y: number; vx: number; vy: number; r: number;
  alpha: number; alphaDir: number; glow: boolean;
}

export default function AnimatedBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let particles: Particle[] = [];
    let w = 0;
    let h = 0;
    let gridOffset = 0;

    function resize() {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas!.width = w;
      canvas!.height = h;
      initParticles();
    }

    function initParticles() {
      // Dense particle field — 1 per 6000px
      const count = Math.floor((w * h) / 6000);
      particles = Array.from({ length: count }, (_, i) => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.3,
        vy: -0.2 - Math.random() * 0.5,
        r: Math.random() * 1.5 + 0.5,
        alpha: Math.random() * 0.7 + 0.15,
        alphaDir: (Math.random() - 0.5) * 0.006,
        glow: i % 7 === 0, // every 7th particle is a "glow" particle
      }));
    }

    function drawGrid() {
      const step = 80;
      ctx!.strokeStyle = 'rgba(0, 194, 255, 0.09)';
      ctx!.lineWidth = 0.6;

      const offsetY = gridOffset % step;
      const offsetX = (gridOffset * 0.3) % step;

      for (let x = offsetX; x < w; x += step) {
        ctx!.beginPath(); ctx!.moveTo(x, 0); ctx!.lineTo(x, h); ctx!.stroke();
      }
      for (let y = offsetY; y < h; y += step) {
        ctx!.beginPath(); ctx!.moveTo(0, y); ctx!.lineTo(w, y); ctx!.stroke();
      }

      // Accent lines — every 400px
      const accentStep = step * 5;
      const accentY = (offsetY * 2) % accentStep;
      ctx!.strokeStyle = 'rgba(0, 194, 255, 0.16)';
      ctx!.lineWidth = 1;
      for (let y = accentY; y < h; y += accentStep) {
        ctx!.beginPath(); ctx!.moveTo(0, y); ctx!.lineTo(w, y); ctx!.stroke();
      }

      // Scanning line — sweeps top to bottom
      const scanY = (gridOffset * 3) % h;
      const scanGrad = ctx!.createLinearGradient(0, scanY - 30, 0, scanY + 30);
      scanGrad.addColorStop(0, 'rgba(0, 194, 255, 0)');
      scanGrad.addColorStop(0.5, 'rgba(0, 194, 255, 0.06)');
      scanGrad.addColorStop(1, 'rgba(0, 194, 255, 0)');
      ctx!.fillStyle = scanGrad;
      ctx!.fillRect(0, scanY - 30, w, 60);
    }

    function drawParticles() {
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.alpha += p.alphaDir;

        if (p.alpha >= 0.85) p.alphaDir = -0.004;
        if (p.alpha <= 0.1) p.alphaDir = 0.004;

        if (p.y < -20) { p.y = h + 20; p.x = Math.random() * w; }
        if (p.x < -20) p.x = w + 20;
        if (p.x > w + 20) p.x = -20;

        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.r, 0, Math.PI * 2);

        if (p.glow) {
          // Glow particle — larger, with outer glow
          const grad = ctx!.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 3);
          grad.addColorStop(0, `rgba(0, 194, 255, ${p.alpha * 0.95})`);
          grad.addColorStop(1, 'rgba(0, 194, 255, 0)');
          ctx!.fillStyle = grad;
          ctx!.beginPath();
          ctx!.arc(p.x, p.y, p.r * 3, 0, Math.PI * 2);
          ctx!.fill();
        } else {
          ctx!.fillStyle = `rgba(0, 194, 255, ${p.alpha})`;
          ctx!.fill();
        }

        // Connecting lines — closer distance, brighter
        for (let j = i + 1; j < Math.min(i + 8, particles.length); j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx!.beginPath();
            ctx!.moveTo(p.x, p.y);
            ctx!.lineTo(q.x, q.y);
            ctx!.strokeStyle = `rgba(0, 194, 255, ${0.08 * (1 - dist / 150)})`;
            ctx!.lineWidth = 0.4;
            ctx!.stroke();
          }
        }
      }
    }

    function drawVignette() {
      const gradient = ctx!.createRadialGradient(w / 2, h / 2, w * 0.4, w / 2, h / 2, w * 0.85);
      gradient.addColorStop(0, 'rgba(10, 14, 20, 0)');
      gradient.addColorStop(1, 'rgba(10, 14, 20, 0.55)');
      ctx!.fillStyle = gradient;
      ctx!.fillRect(0, 0, w, h);
    }

    function animate() {
      ctx!.clearRect(0, 0, w, h);
      gridOffset += 0.1;
      drawGrid();
      drawParticles();
      drawVignette();
      animId = requestAnimationFrame(animate);
    }

    resize();
    window.addEventListener('resize', resize);
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0, background: '#0A0E14' }}
    />
  );
}
